"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Spinner } from "@/components/ui/spinner";
import { useTranslations } from "@/hooks/useTranslations";

type QuickJobFormData = {
  title: string;
  clientId: string;
  assignedToId?: string;
  scheduledDate?: string;
};

interface Client {
  id: string;
  name: string;
}

interface User {
  id: string;
  name: string | null;
  email: string;
}

interface QuickJobModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function QuickJobModal({ isOpen, onClose }: QuickJobModalProps) {
  const router = useRouter();
  const { toast } = useToast();
  const t = useTranslations();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const quickJobSchema = z.object({
    title: z.string().min(1, t("quickActions.job.titleRequired")),
    clientId: z.string().min(1, t("quickActions.job.clientRequired")),
    assignedToId: z.string().optional(),
    scheduledDate: z.string().optional(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<QuickJobFormData>({
    resolver: zodResolver(quickJobSchema),
    defaultValues: {
      title: "",
      clientId: "",
      assignedToId: "",
      scheduledDate: "",
    },
  });

  const clientId = watch("clientId");
  const assignedToId = watch("assignedToId");

  // Fetch clients and users when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoadingData(true);
      Promise.all([
        fetch("/api/clients").then((res) => {
          if (!res.ok) throw new Error("Failed to fetch clients");
          return res.json();
        }),
        fetch("/api/users").then((res) => {
          if (!res.ok) throw new Error("Failed to fetch users");
          return res.json();
        }),
      ])
        .then(([clientsResult, usersData]) => {
          const clientsData = clientsResult.data || clientsResult; // Handle both new and old format
          setClients(clientsData);
          setUsers(usersData);
        })
        .catch((error) => {
          console.error("Error loading data:", error);
          toast({
            title: t("quickActions.job.toast.error"),
            description: t("quickActions.job.toast.loadError"),
            variant: "destructive",
          });
        })
        .finally(() => {
          setIsLoadingData(false);
        });
    }
  }, [isOpen, toast, t]);

  const onSubmit = async (data: QuickJobFormData) => {
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/jobs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: data.title,
          clientId: data.clientId,
          assignedToId: data.assignedToId || null,
          status: "PENDING",
          description: "",
          tradeSpecificData: {},
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create job");
      }

      const job = await response.json();

      toast({
        title: t("quickActions.job.toast.created"),
        description: t("quickActions.job.toast.createdDescription", { title: data.title }),
      });

      reset();
      onClose();
      router.push(`/jobs/${job.id}`);
      router.refresh();
    } catch (error) {
      console.error("Error creating job:", error);
      toast({
        title: t("quickActions.job.toast.error"),
        description: t("quickActions.job.toast.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("quickActions.job.title")}</DialogTitle>
          <DialogDescription>
            {t("quickActions.job.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoadingData ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="md" label={t("quickActions.job.loadingFormData")} />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">
                {t("quickActions.job.titleLabel")} <span className="text-error">*</span>
              </Label>
              <Input
                id="title"
                {...register("title")}
                placeholder={t("quickActions.job.titlePlaceholder")}
                disabled={isSubmitting}
              />
              {errors.title && (
                <p className="text-sm text-error">{errors.title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientId">
                {t("quickActions.job.clientLabel")} <span className="text-error">*</span>
              </Label>
              <Select
                value={clientId}
                onValueChange={(value) => setValue("clientId", value)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="clientId">
                  <SelectValue placeholder={t("quickActions.job.selectClient")} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.clientId && (
                <p className="text-sm text-error">{errors.clientId.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scheduledDate">{t("quickActions.job.scheduledDateLabel")}</Label>
                <Input
                  id="scheduledDate"
                  type="date"
                  {...register("scheduledDate")}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="assignedToId">{t("quickActions.job.assignToLabel")}</Label>
                <Select
                  value={assignedToId || "unassigned"}
                  onValueChange={(value) =>
                    setValue("assignedToId", value === "unassigned" ? "" : value)
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="assignedToId">
                    <SelectValue placeholder={t("quickActions.job.unassigned")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">{t("quickActions.job.unassigned")}</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name || user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
                {isSubmitting && <Spinner size="sm" className="mr-2" />}
                {isSubmitting ? t("quickActions.job.creating") : t("quickActions.job.createButton")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
